import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import {
  fetchStatusBoard,
  changeShipmentStatus,
  archiveShipment,
} from '../../store/slices/shipmentTrackerSlice';
import { addNotification } from '../../store/slices/notificationSlice';
import { useShipmentTrackerPermissions } from '../../hooks/useShipmentTrackerPermissions';
import ShipmentBoard, { BOARD_STATUSES } from '../../components/shipment-tracker/ShipmentBoard';
import ShipmentStatusSwitcher from '../../components/shipment-tracker/ShipmentStatusSwitcher';
import ShipmentCard from '../../components/shipment-tracker/ShipmentCard';
import ShipmentBoardMoreMenu from '../../components/shipment-tracker/ShipmentBoardMoreMenu';
import ShipmentBoardLabelFilter from '../../components/shipment-tracker/ShipmentBoardLabelFilter';
import type { ShipmentListItemView } from '../../api/generated/models/ShipmentListItemView';
import type { ShipmentStatusRequest } from '../../api/generated/models/ShipmentStatusRequest';

function filterColumnsByLabels(
  columns: Record<string, ShipmentListItemView[]>,
  labelIds: string[],
): Record<string, ShipmentListItemView[]> {
  if (labelIds.length === 0) return columns;
  const out: Record<string, ShipmentListItemView[]> = {};
  for (const [status, items] of Object.entries(columns)) {
    out[status] = items.filter((s) =>
      (s.labels ?? []).some((l) => labelIds.includes(l.id)),
    );
  }
  return out;
}

const ShipmentTrackerStatusBoardPage: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const { canWrite } = useShipmentTrackerPermissions();
  const { columns, loading, error } = useAppSelector(
    (state) => state.shipmentTracker.statusBoard,
  );
  const labelCatalog = useAppSelector((state) => state.shipmentTracker.labels.items);

  const labelFilter = useMemo(() => {
    const raw = searchParams.get('labels');
    if (!raw) return [] as string[];
    return raw.split(',').filter(Boolean);
  }, [searchParams]);

  const setLabelFilter = useCallback(
    (ids: string[]) => {
      if (ids.length === 0) {
        setSearchParams({});
      } else {
        setSearchParams({ labels: ids.join(',') });
      }
    },
    [setSearchParams],
  );

  const filteredColumns = useMemo(
    () => filterColumnsByLabels(columns, labelFilter),
    [columns, labelFilter],
  );

  const [mobileStatus, setMobileStatus] = useState<(typeof BOARD_STATUSES)[number]>(
    BOARD_STATUSES[0],
  );

  useEffect(() => {
    dispatch(fetchStatusBoard());
  }, [dispatch]);

  const handleChangeStatus = useCallback(
    async (id: string, status: ShipmentStatusRequest.executionStatus) => {
      await dispatch(changeShipmentStatus({ id, executionStatus: status })).unwrap();
    },
    [dispatch],
  );

  const handleArchive = useCallback(
    async (id: string) => {
      try {
        await dispatch(archiveShipment(id)).unwrap();
        dispatch(
          addNotification({ type: 'success', title: t('success'), message: t('shipment_archived') }),
        );
      } catch {
        // error notification fired in thunk
      }
    },
    [dispatch, t],
  );

  if (loading) return <div className="page-loading">{t('loading')}</div>;
  if (error) return <div className="page-error">{error}</div>;

  const mobileShipments = filteredColumns[mobileStatus] ?? [];

  return (
    <div className="shipment-board-page">
      <div className="shipment-board-header">
        <h1>{t('board_title')}</h1>
        <div className="shipment-board-header__actions">
          <ShipmentBoardLabelFilter
            labels={labelCatalog}
            selected={labelFilter}
            onChange={setLabelFilter}
          />
          <ShipmentBoardMoreMenu />
        </div>
      </div>

      <div className="shipment-board-body">
        <div className="shipment-mobile-view">
          <ShipmentStatusSwitcher
            activeStatus={mobileStatus}
            onChange={setMobileStatus}
          />
          <section className="shipment-mobile-panel">
            <header className="shipment-column__header">
              <h2 className="shipment-column__title">{t(`shipment_status_${mobileStatus}`)}</h2>
              <span className="shipment-column__count">{mobileShipments.length}</span>
            </header>
            <div className="shipment-column__body">
              {mobileShipments.map((s) => (
                <ShipmentCard
                  key={s.id}
                  shipment={s}
                  canWrite={canWrite}
                  onChangeStatus={handleChangeStatus}
                  onArchive={handleArchive}
                />
              ))}
            </div>
          </section>
        </div>

        <ShipmentBoard
          columns={filteredColumns}
          canWrite={canWrite}
          onChangeStatus={handleChangeStatus}
          onArchive={handleArchive}
        />
      </div>
    </div>
  );
};

export default ShipmentTrackerStatusBoardPage;
